<!-- src/views/Gym.vue -->
<script setup lang="ts">
import { ref, onMounted, watchEffect, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useContentStore } from '@/stores/content.store'
import { useHeroStore } from '@/stores/hero.store'
import { isNotFoundRoute, notFoundLocationForRoute } from '@/router/notFound'

import {
  getComponentClasses,
  normalizeBlocks,
  resolveComponentByType,
  type AcfFlexItem,
  type PageBlock,
} from '@/utils/components'
import type { CptModel } from '@/models/cpt'

const router = useRouter()
const route = useRoute()

const contentStore = useContentStore()
const { gyms, isLoading, loading } = storeToRefs(contentStore)

const heroStore = useHeroStore()

// type CptModel = (typeof gyms.value)[number]
const post = ref<CptModel | null>(null)

/**
 * Finds a post based on the slug in the URL.
 */
function resolvePostFromRoute() {
  // Wait until global init is finished
  if (isLoading.value) return

  // If posts are in local loading (force refresh, etc.)
  if (loading.value.gyms) return
  if (!contentStore.hasLoaded('gyms')) return

  const slug = typeof route.params.slug === 'string'
    ? route.params.slug
    : ''

  if (!slug) return

  const match = gyms.value.find((p) => p.slug === slug)

  if (!match) {
    if (!contentStore.hasFullCoverage('gyms')) {
      void contentStore.ensureFullResource('gyms')
      return
    }

    // Avoid a redirect loop
    if (!isNotFoundRoute(route)) {
      void router.replace(notFoundLocationForRoute(route, { slug }))
    }
    return
  }

  post.value = match
  heroStore.setHeroFromSegment(match)
}

/**
 * components can be undefined/false -> always an array in the template
 */
const componentBlocks = computed<PageBlock[]>(() => {
  const raw = post.value?.components
  return Array.isArray(raw) ? normalizeBlocks(raw as AcfFlexItem[]) : []
})

onMounted(async () => {
  await contentStore.ensureRouteResource('gyms')
})

// Reacts to: isLoading, loading.posts, posts, route.params.slug
watchEffect(() => {
  resolvePostFromRoute()
})
</script>

<template>
  <keep-alive>
    <component
      v-for="(block, index) in componentBlocks"
      :key="block.id"
      :is="resolveComponentByType(block.type, 'location')"
      :block="block"
      :data="block"
      :data-key="index"
      :data-component="block.type"
      :data-uuid="block.raw.uuid"
      :class="getComponentClasses(block)"
    />
  </keep-alive>
</template>
